
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Link } from 'react-router';
import {
  ArrowRight,
  ArrowLeft,
  Upload,
  Sparkles,
  Loader2,
  Users,
  Check,
  Plus,
  Settings2,
  CheckCircle2,
  Trash2,
} from 'lucide-react';
import { apiUpload } from '@/lib/api';
import { addLeadGroupsToCampaign, removeLeadGroupsFromCampaign, getCampaignLeadGroups } from '@/lib/campaigns';
import { getLeadGroups, createLeadGroup, deleteLeadGroup, type LeadGroup } from '@/lib/lead-groups';
import { useToast } from '@/hooks/useToast';
import { AIProgressBar } from '@/components/AIProgressBar';
import { UploadModal } from '@/components/UploadModal';
import { AIGenerateModal } from '@/components/AIGenerateModal';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import type { LeadImportResult } from '@hypema/shared';

function sourceBadge(source: string) {
  const colors: Record<string, string> = {
    ai: 'bg-violet-500/10 text-violet-500',
    csv: 'bg-blue-500/10 text-blue-500',
    manual: 'bg-base-content/10 text-base-content/50',
  };
  const labels: Record<string, string> = { ai: 'AI', csv: 'CSV', manual: 'Manual' };
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${colors[source] || colors.manual}`}>
      {labels[source] || source}
    </span>
  );
}

const TARGET_FIELDS = [
  { key: 'email', label: 'Email', required: true },
  { key: 'firstName', label: 'First Name', required: false },
  { key: 'lastName', label: 'Last Name', required: false },
  { key: 'company', label: 'Company', required: false },
  { key: 'roleTitle', label: 'Role / Title', required: false },
  { key: 'country', label: 'Country', required: false },
  { key: 'notes', label: 'Notes', required: false },
];

interface MappingState {
  file: File;
  mappings: Record<string, string | null>;
  headers: string[];
  sampleData: Record<string, string>[];
  confidence: number;
  totalRows: number;
}

export function CampaignLeadsPage() {
  const params = useParams();
  const campaignId = params.id as string;
  const toast = useToast();
  const [allGroups, setAllGroups] = useState<LeadGroup[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);

  // Modals
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [mappingState, setMappingState] = useState<MappingState | null>(null);

  async function loadData() {
    setLoading(true);
    try {
      const [groups, campaignGroups] = await Promise.all([
        getLeadGroups(),
        getCampaignLeadGroups(campaignId),
      ]);
      groups.sort((a, b) => {
        const aSent = a.sentCount > 0 && a.sentCount >= a.leadCount ? 1 : 0;
        const bSent = b.sentCount > 0 && b.sentCount >= b.leadCount ? 1 : 0;
        return aSent - bSent;
      });
      setAllGroups(groups);
      const selectedGroupIds = new Set((campaignGroups as LeadGroup[]).map((g) => g.id));
      setSelectedIds(selectedGroupIds);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load lead groups');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadData(); }, []);

  async function toggleGroup(groupId: string) {
    const wasSelected = selectedIds.has(groupId);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (wasSelected) next.delete(groupId);
      else next.add(groupId);
      return next;
    });

    try {
      if (wasSelected) {
        await removeLeadGroupsFromCampaign(campaignId, [groupId]);
      } else {
        await addLeadGroupsToCampaign(campaignId, [groupId]);
      }
    } catch {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (wasSelected) next.add(groupId);
        else next.delete(groupId);
        return next;
      });
      toast.error('Failed to update selection');
    }
  }

  async function handleDeleteGroup(groupId: string, groupName: string) {
    try {
      await deleteLeadGroup(groupId);
      setAllGroups((prev) => prev.filter((g) => g.id !== groupId));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(groupId);
        return next;
      });
      toast.success(`Deleted "${groupName}"`);
    } catch {
      toast.error('Failed to delete group');
    }
  }

  async function handleFileSelect(file: File) {
    setAnalyzing(true);
    try {
      const result = await apiUpload<{
        mappings: Record<string, string | null>;
        headers: string[];
        sampleData: Record<string, string>[];
        confidence: number;
        totalRows: number;
      }>('/ai/map-csv-columns', file);

      setMappingState({
        file,
        mappings: result.mappings,
        headers: result.headers,
        sampleData: result.sampleData,
        confidence: result.confidence,
        totalRows: result.totalRows,
      });
    } catch (err: any) {
      toast.error(err.message || 'Failed to analyze columns');
    } finally {
      setAnalyzing(false);
    }
  }

  async function handleConfirmMapping() {
    if (!mappingState) return;
    setImporting(true);
    try {
      const group = await createLeadGroup({
        name: mappingState.file.name.replace(/\.(csv|xlsx|xls)$/i, ''),
        source: 'csv',
      });
      const result = await apiUpload<LeadImportResult>(
        `/leads/import?leadGroupId=${group.id}`,
        mappingState.file,
        'file',
        { columnMapping: JSON.stringify(mappingState.mappings) },
      );
      if (result.imported > 0) {
        toast.success(`Imported ${result.imported} leads${result.skippedDuplicate ? ` (${result.skippedDuplicate} duplicates skipped)` : ''}`);
      } else if (result.skippedDuplicate > 0) {
        toast.error(`All ${result.skippedDuplicate} leads already exist (duplicates)`);
      } else if (result.skippedInvalid > 0) {
        toast.error(`${result.skippedInvalid} leads had invalid or missing email addresses`);
      } else {
        toast.error('No leads could be imported');
      }
      // Auto-select the new group for this campaign
      await addLeadGroupsToCampaign(campaignId, [group.id]);
      setMappingState(null);
      await loadData();
    } catch (err: any) {
      toast.error(err.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  }

  function updateMapping(targetField: string, csvHeader: string | null) {
    if (!mappingState) return;
    setMappingState({
      ...mappingState,
      mappings: { ...mappingState.mappings, [targetField]: csvHeader },
    });
  }

  async function handleAiComplete(data: { leads: any[]; count: number; leadGroupId: string }) {
    toast.success(`Generated ${data.count} leads`);
    setShowAiModal(false);
    if (data.leadGroupId) {
      await addLeadGroupsToCampaign(campaignId, [data.leadGroupId]);
    }
    await loadData();
  }

  const selectedLeadCount = allGroups.filter((g) => selectedIds.has(g.id)).reduce((sum, g) => sum + g.leadCount, 0);

  // Column mapping review UI
  if (mappingState) {
    return (
      <div className="space-y-6 max-w-4xl mx-auto w-full animate-fade-in">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Map Columns</h1>
          <p className="text-sm text-base-content/40 mt-1">
            We analyzed <span className="font-medium text-base-content/60">{mappingState.file.name}</span> ({mappingState.totalRows} lead{mappingState.totalRows !== 1 ? 's' : ''}) and mapped the columns.
            Review and adjust if needed.
          </p>
        </div>

        <div className="flex items-center gap-2 text-xs">
          <CheckCircle2 size={14} className={mappingState.confidence > 0.7 ? 'text-success' : 'text-warning'} />
          <span className="text-base-content/50">
            AI confidence: {Math.round(mappingState.confidence * 100)}%
          </span>
        </div>

        <div className="glass-card rounded-xl overflow-hidden">
          <table className="table table-sm w-full">
            <thead>
              <tr className="border-b border-base-300/30">
                <th className="text-xs text-base-content/40 font-medium">OUR FIELD</th>
                <th className="text-xs text-base-content/40 font-medium">YOUR COLUMN</th>
                <th className="text-xs text-base-content/40 font-medium">SAMPLE DATA</th>
              </tr>
            </thead>
            <tbody>
              {TARGET_FIELDS.map((field) => {
                const selectedHeader = mappingState.mappings[field.key] || '';
                const sampleValue = selectedHeader && mappingState.sampleData[0]
                  ? mappingState.sampleData[0][selectedHeader] || '—'
                  : '—';
                return (
                  <tr key={field.key} className="border-b border-base-300/20">
                    <td className="py-3">
                      <span className="text-sm font-medium">{field.label}</span>
                      {field.required && <span className="text-error ml-1 text-xs">*</span>}
                    </td>
                    <td className="py-3">
                      <select
                        value={selectedHeader}
                        onChange={(e) => updateMapping(field.key, e.target.value || null)}
                        className="select select-bordered select-sm w-full max-w-xs"
                      >
                        <option value="">— Skip —</option>
                        {mappingState.headers.map((h) => (
                          <option key={h} value={h}>{h}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-3">
                      <span className="text-xs text-base-content/40 font-mono">{sampleValue}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {mappingState.sampleData.length > 1 && (
          <details className="text-xs">
            <summary className="text-base-content/40 cursor-pointer hover:text-base-content/60 transition-colors">
              Preview more rows ({mappingState.sampleData.length} total)
            </summary>
            <div className="mt-2 overflow-x-auto glass-card rounded-lg p-3">
              <table className="table table-xs w-full">
                <thead>
                  <tr>
                    {mappingState.headers.map((h) => (
                      <th key={h} className="text-[10px] text-base-content/30">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {mappingState.sampleData.map((row, i) => (
                    <tr key={i}>
                      {mappingState.headers.map((h) => (
                        <td key={h} className="text-[10px] text-base-content/50 font-mono">{row[h] || ''}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        )}

        <div className="flex items-center gap-3">
          <button
            onClick={handleConfirmMapping}
            disabled={importing || !mappingState.mappings.email}
            className="btn btn-primary btn-sm gap-1"
          >
            {importing ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
            {importing ? 'Importing...' : 'Import & Select'}
          </button>
          <button onClick={() => setMappingState(null)} className="btn btn-ghost btn-sm">
            Cancel
          </button>
          {!mappingState.mappings.email && (
            <span className="text-xs text-error">Email column mapping is required</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto w-full animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Select Leads</h1>
          <p className="text-sm text-base-content/40 mt-1">
            {selectedIds.size} of {allGroups.length} group{allGroups.length !== 1 ? 's' : ''} selected ({selectedLeadCount} lead{selectedLeadCount !== 1 ? 's' : ''})
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowUploadModal(true)} disabled={analyzing || importing}
            className="btn btn-sm btn-ghost gap-1">
            <Upload size={14} /> {analyzing ? 'Analyzing...' : importing ? 'Importing...' : 'Upload CSV'}
          </button>
          <button onClick={() => setShowAiModal(true)} className="btn btn-sm btn-primary gap-1">
            <Sparkles size={14} /> AI Generate
          </button>
        </div>
      </div>

      {/* Analyzing indicator */}
      {analyzing && (
        <AIProgressBar
          active={true}
          size="sm"
          steps={['Reading file columns...', 'Analyzing column names...', 'Mapping to lead fields...']}
        />
      )}

      {/* Content */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <div key={i} className="h-28 animate-shimmer rounded-xl" />)}
        </div>
      ) : allGroups.length === 0 ? (
        <div className="grid grid-cols-2 gap-6">
          <button onClick={() => setShowAiModal(true)}
            className="glass-card rounded-xl p-10 border-2 border-dashed border-base-300/50 hover:border-primary/30 transition-colors flex flex-col items-center justify-center gap-3 text-center">
            <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
              <Sparkles size={24} className="text-primary" />
            </div>
            <p className="text-base font-medium">AI Generate Leads</p>
            <p className="text-sm text-base-content/40">Tell us your target audience and we find prospects</p>
          </button>
          <button onClick={() => setShowUploadModal(true)}
            className="glass-card rounded-xl p-10 border-2 border-dashed border-base-300/50 hover:border-primary/30 transition-colors flex flex-col items-center justify-center gap-3 text-center cursor-pointer">
            <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
              <Upload size={24} className="text-primary" />
            </div>
            <p className="text-base font-medium">Upload Your Own</p>
            <p className="text-sm text-base-content/40">Upload a CSV or Excel file and we organize them</p>
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {allGroups.map((group) => {
            const selected = selectedIds.has(group.id);
            return (
              <div
                key={group.id}
                onClick={() => toggleGroup(group.id)}
                className={`glass-card rounded-xl p-5 space-y-3 animate-fade-in cursor-pointer transition-all group/card relative ${
                  selected ? 'ring-1 ring-primary/30 bg-primary/5' : 'hover:shadow-md'
                }`}
              >
                <button
                  onClick={(e) => { e.stopPropagation(); setDeleteTarget({ id: group.id, name: group.name }); }}
                  className="absolute top-3 right-3 opacity-0 group-hover/card:opacity-100 transition-opacity btn btn-ghost btn-xs text-base-content/20 hover:text-error z-10"
                  title="Delete group"
                >
                  <Trash2 size={12} />
                </button>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleGroup(group.id); }}
                    className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all shrink-0 ${
                      selected ? 'bg-primary border-primary text-primary-content' : 'border-base-300/50 hover:border-primary/50'
                    }`}
                  >
                    {selected && <Check size={12} />}
                  </button>
                  <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Users size={16} className="text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold truncate">{group.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {sourceBadge(group.source)}
                      <span className="text-[10px] text-base-content/30">{group.leadCount} lead{group.leadCount !== 1 ? 's' : ''}</span>
                      {group.sentCount > 0 && group.sentCount >= group.leadCount ? (
                        <span className="text-[10px] text-green-500 font-medium flex items-center gap-0.5">✓ All Done</span>
                      ) : group.sentCount > 0 ? (
                        <span className="text-[10px] text-green-500/70">{group.sentCount}/{group.leadCount} Done</span>
                      ) : null}
                    </div>
                  </div>
                </div>
                <div className="flex items-center">
                  <Link
                    to={`/leads/${group.id}?from=campaign&campaignId=${campaignId}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-base-content/40 hover:text-primary transition-colors"
                    title="View leads in group"
                  >
                    <Settings2 size={13} />
                  </Link>
                </div>
              </div>
            );
          })}

        </div>
      )}

      {/* Navigation */}
      <div className="flex gap-3">
        <Link to={`/campaign/${campaignId}/setup`} className="btn btn-ghost gap-1">
          <ArrowLeft size={14} /> Setup
        </Link>
        <div className="flex-1" />
        <Link to={`/campaign/${campaignId}/review`} className="btn btn-primary gap-2">
          Review & Draft <ArrowRight size={16} />
        </Link>
      </div>

      {/* AI Generate modal */}
      <AIGenerateModal
        open={showAiModal}
        onClose={() => setShowAiModal(false)}
        onComplete={handleAiComplete}
      />

      {/* Upload modal */}
      <UploadModal
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onFileSelect={handleFileSelect}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete lead group?"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? All leads in this group will be removed.`}
        onConfirm={() => {
          if (deleteTarget) handleDeleteGroup(deleteTarget.id, deleteTarget.name);
          setDeleteTarget(null);
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
